"use client"

import { useLocale, useTranslations } from "next-intl"
import { useRouter } from "next/navigation"
import { Button } from "@/components/ui/button"
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select"
import { LOCALES } from "@/i18n/routing"
import { useState } from "react"

interface LanguageSwitcherProps {
	variant?: "desktop" | "mobile"
}

export function LanguageSwitcher({
	variant = "desktop",
}: LanguageSwitcherProps) {
	const t = useTranslations("home.navigation")
	const locale = useLocale()
	const router = useRouter()
	const [isChanging, setIsChanging] = useState(false)

	const handleLanguageChange = async (newLocale: string) => {
		if (newLocale === locale || isChanging) return

		setIsChanging(true)

		try {
			// Set the locale cookie
			document.cookie = `qaderoon_locale=${newLocale}; path=/; max-age=31536000`

			// Reload the page to apply the new locale
			router.refresh()
		} catch (error) {
			console.error("Failed to change language:", error)
		} finally {
			setIsChanging(false)
		}
	}

	if (variant === "mobile") {
		return (
			<div className="pt-3 border-t">
				<label className="block text-sm font-medium text-gray-700 mb-2">
					{t("language")}
				</label>
				<Select
					value={locale}
					onValueChange={handleLanguageChange}
					disabled={isChanging}
				>
					<SelectTrigger className="w-full">
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						{LOCALES.map((loc) => (
							<SelectItem key={loc} value={loc}>
								{loc === "ar" ? t("arabic") : t("english")}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</div>
		)
	}

	return (
		<Select
			value={locale}
			onValueChange={handleLanguageChange}
			disabled={isChanging}
		>
			<SelectTrigger className="w-32">
				<SelectValue />
			</SelectTrigger>
			<SelectContent>
				{LOCALES.map((loc) => (
					<SelectItem key={loc} value={loc}>
						{loc === "ar" ? t("arabic") : t("english")}
					</SelectItem>
				))}
			</SelectContent>
		</Select>
	)
}
